import { chat, isClaudeError } from '@sazeo/claude';
import { type SearchResult, useSearch } from '@sazeo/sdk';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  cn,
} from '@sazeo/ui';
import { Bot, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type Mode = 'search' | 'ask';

export interface CmdKDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useCmdKShortcut(): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'k') return;
      if (!e.metaKey && !e.ctrlKey) return;
      e.preventDefault();
      setIsOpen((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return { isOpen, open, close };
}

export function CmdKDialog({ isOpen, onOpenChange }: CmdKDialogProps) {
  const { t } = useTranslation('core.cmdK');
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('search');
  const [query, setQuery] = useState('');
  const [askInput, setAskInput] = useState('');
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: searchData } = useSearch(query);

  useEffect(() => {
    if (!isOpen) {
      setMode('search');
      setQuery('');
      setAskInput('');
      setAiResponse(null);
      setHasError(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'ask') {
      textareaRef.current?.focus();
    }
  }, [mode]);

  const handleSearchSelect = useCallback(
    (result: SearchResult) => {
      onOpenChange(false);
      navigate(result.url);
    },
    [navigate, onOpenChange],
  );

  const handleAsk = useCallback(async () => {
    const q = askInput.trim();
    if (!q || isLoading) return;
    setIsLoading(true);
    setAiResponse(null);
    setHasError(false);
    const result = await chat({ messages: [{ role: 'user', content: q }] });
    setIsLoading(false);
    if (isClaudeError(result)) {
      setHasError(true);
    } else {
      setAiResponse(result.message.content);
    }
  }, [askInput, isLoading]);

  const handleAskKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleAsk();
      }
    },
    [handleAsk],
  );

  const allResults = searchData.total > 0 ? Object.values(searchData.grouped).flat() : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cmdk-dialog" className="overflow-hidden p-0 max-w-lg">
        <div className="flex border-b border-[var(--color-border)]">
          <button
            type="button"
            data-testid="cmdk-mode-search"
            onClick={() => setMode('search')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[12px] font-medium transition-colors',
              mode === 'search'
                ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <Search size={13} aria-hidden />
            {t('mode_search')}
          </button>
          <button
            type="button"
            data-testid="cmdk-mode-ask"
            onClick={() => setMode('ask')}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 py-2.5 text-[12px] font-medium transition-colors',
              mode === 'ask'
                ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-text-primary)]'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <Bot size={13} aria-hidden />
            {t('mode_ask')}
          </button>
        </div>

        {mode === 'search' ? (
          <Command
            shouldFilter={false}
            className={cn(
              '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium',
              '[&_[cmdk-group-heading]]:text-[var(--color-text-secondary)]',
              '[&_[cmdk-input]]:h-11 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5',
              '[&_[cmdk-item]_svg]:size-4',
            )}
          >
            <CommandInput
              placeholder={t('dialog_placeholder')}
              value={query}
              onValueChange={setQuery}
              data-testid="cmdk-search-input"
            />
            <CommandList>
              {query.trim().length === 0 || allResults.length === 0 ? (
                <CommandEmpty>{t('no_results')}</CommandEmpty>
              ) : (
                allResults.map((result: SearchResult) => (
                  <CommandItem
                    key={`${result.entity_id}`}
                    value={`${result.entity_id}:${result.title}`}
                    onSelect={() => handleSearchSelect(result)}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[13px]">{result.title}</span>
                      {result.subtitle && (
                        <span className="truncate text-[11px] text-[var(--color-text-tertiary)]">
                          {result.subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))
              )}
            </CommandList>
          </Command>
        ) : (
          <div className="flex flex-col gap-3 p-4">
            <textarea
              ref={textareaRef}
              value={askInput}
              onChange={(e) => setAskInput(e.target.value)}
              onKeyDown={handleAskKeyDown}
              placeholder={t('ask_placeholder')}
              rows={3}
              data-testid="cmdk-ask-textarea"
              className={cn(
                'w-full resize-none rounded-[6px] border border-[var(--color-border)]',
                'bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text-primary)]',
                'placeholder:text-[var(--color-text-tertiary)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]',
              )}
            />
            <button
              type="button"
              onClick={() => void handleAsk()}
              disabled={isLoading || !askInput.trim()}
              className={cn(
                'inline-flex h-8 items-center justify-center rounded-[6px] px-4 text-[13px] font-medium',
                'bg-[var(--color-accent)] text-[var(--color-bg)]',
                'hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed',
                'transition-opacity',
              )}
            >
              {t('mode_ask')}
            </button>

            {isLoading && (
              <p
                className="text-[12px] text-[var(--color-text-secondary)]"
                aria-live="polite"
                data-testid="cmdk-ask-loading"
              >
                {t('loading')}
              </p>
            )}

            {hasError && (
              <p
                className="text-[12px] text-[var(--color-error)]"
                aria-live="assertive"
                data-testid="cmdk-ask-error"
              >
                {t('error')}
              </p>
            )}

            {aiResponse && (
              <div
                className={cn(
                  'rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface)]',
                  'px-3 py-2.5 text-[13px] text-[var(--color-text-primary)] whitespace-pre-wrap',
                )}
                data-testid="cmdk-ask-response"
                aria-live="polite"
              >
                {aiResponse}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
